import type { HelpAudience } from '../../api/help';

/**
 * The screens a help article's button can open, by name, per app.
 *
 * An article stores a route (`/(app)/security-settings`), and each app only
 * follows routes on its own allowlist — anything else hides the button. So
 * these lists are copies of those allowlists, with the names people know the
 * screens by:
 *
 *   - consumer — payiq-consumer `lib/articleDestinations.ts`
 *   - business — payiq-business `lib/articleDestinations.ts`
 *
 * Keep them in step. A screen listed here but not in the app is a button that
 * silently never shows; one in the app but not here is one nobody can pick.
 *
 * Only screens that open with no parameters: an article is written once for
 * everybody, so it cannot point at one card or one Ajo group.
 */
export type AppName = 'consumer' | 'business';

export interface AppScreen {
  app: AppName;
  route: string;
  name: string;
  group: string;
}

const consumer = (group: string, name: string, route: string): AppScreen => ({
  app: 'consumer',
  group,
  name,
  route,
});
const business = (group: string, name: string, route: string): AppScreen => ({
  app: 'business',
  group,
  name,
  route,
});

export const APP_SCREENS: AppScreen[] = [
  consumer('Money', 'Send money', '/(app)/send-money'),
  consumer('Money', 'Receive money', '/(app)/receive'),
  consumer('Money', 'Fund account', '/(app)/fund-account'),
  consumer('Money', 'Saved beneficiaries', '/(app)/beneficiaries'),
  consumer('Money', 'Transfer limits', '/(app)/transfer-limits'),
  consumer('Money', 'Payment requests', '/(app)/requests'),
  consumer('Money', 'Split bill', '/(app)/splits'),
  consumer('Money', 'Scan', '/(app)/scan'),
  consumer('Money', 'Airtime & data', '/(app)/airtime'),
  consumer('Money', 'Pay bills', '/(app)/bills'),
  consumer('Money', 'Cards', '/(app)/cards'),

  consumer('Savings', 'Savings', '/(app)/savings'),
  consumer('Savings', 'New savings goal', '/(app)/create-savings-goal'),
  consumer('Savings', 'Savings activity', '/(app)/savings-transactions'),
  consumer('Savings', 'Ajo (group savings)', '/(app)/ajo'),
  consumer('Savings', 'Discover public Ajo groups', '/(app)/ajo-discover'),

  consumer('Insights', 'Analytics', '/(app)/analytics'),
  consumer('Insights', 'Review spending categories', '/(app)/categorisation-inbox'),
  consumer('Insights', 'Financial health', '/(app)/health-score'),
  consumer('Insights', 'Net worth', '/(app)/net-worth'),
  consumer('Insights', 'Ibra insights', '/(app)/ibra-insights'),

  consumer('Records', 'Transactions', '/(app)/transactions'),
  consumer('Records', 'Statements', '/(app)/statements-history'),
  consumer('Records', 'Generate a statement', '/(app)/statement-generation'),
  consumer('Records', 'Receipts', '/(app)/receipts'),
  consumer('Records', 'Receipts needing a payment', '/(app)/unmatched-receipts'),

  consumer('Account', 'Your profile & account tier', '/(app)/personal-profile'),
  consumer('Account', 'Settings', '/(app)/settings'),
  consumer('Account', 'Security', '/(app)/security-settings'),
  consumer('Account', 'Change password', '/(app)/change-password'),
  consumer('Account', 'Reset transaction PIN', '/(app)/reset-transaction-pin'),
  consumer('Account', 'Reset Quick Login PIN', '/(app)/reset-login-pin'),
  consumer('Account', 'Quick Login', '/(app)/quick-login-device'),
  consumer('Account', 'Privacy', '/(app)/privacy-settings'),
  consumer('Account', 'Notification settings', '/(app)/notification-settings'),
  consumer('Account', 'Notifications', '/(app)/notifications'),
  consumer('Account', 'App mode (AI or classic)', '/(app)/app-mode'),
  consumer('Account', 'How Ibra talks', '/(app)/coach-preferences'),
  consumer('Account', 'Invite friends', '/(app)/referrals'),
  consumer('Account', 'Your PayIQ tag', '/(app)/payiq-tag'),
  consumer('Account', 'Premium', '/(app)/premium'),

  consumer('Help', 'Chat with support', '/(app)/support-conversation'),
  consumer('Help', 'Help centre', '/(app)/support-home'),
  consumer('Help', 'Terms of service', '/(app)/terms-of-service'),
  consumer('Help', 'Privacy policy', '/(app)/privacy-policy'),

  business('Money', 'Send money', '/(auth)/send-money'),
  business('Money', 'Receive money', '/(auth)/receive'),
  business('Money', 'Fund account', '/(auth)/fund-account'),
  business('Money', 'Saved beneficiaries', '/(auth)/beneficiaries'),
  business('Money', 'Transfer limits', '/(auth)/transfer-limits'),
  business('Money', 'Airtime & bills', '/(auth)/bills'),
  business('Money', 'Cards', '/(auth)/cards'),
  business('Money', 'Payment requests', '/(auth)/payment-requests'),
  business('Money', 'Bulk payments', '/(auth)/bulk-payment-builder'),
  business('Money', 'Scan', '/(auth)/scan'),
  business('Money', 'Accounts', '/(auth)/accounts'),
  business('Sales', 'Invoices', '/(auth)/invoices-list'),
  business('Sales', 'Chase unpaid invoices', '/(auth)/chase-invoices'),
  business('Sales', 'Payment reminders', '/(auth)/payment-reminders'),
  business('Sales', 'Storefront', '/(auth)/storefront-hub'),
  business('Sales', 'Products', '/(auth)/product-catalogue'),
  business('Sales', 'Orders', '/(auth)/orders-list'),
  business('Sales', 'Social selling', '/(auth)/social-connections'),
  business('Sales', 'Social inbox', '/(auth)/social-inbox'),
  business('Spending', 'Suppliers', '/(auth)/supplier-list'),
  business('Spending', 'Supplier bills', '/(auth)/supplier-bills'),
  business('Spending', 'Expense report', '/(auth)/expense-report'),
  business('Spending', 'Spend policies', '/(auth)/spend-policy-list'),
  business('Spending', 'Approvals', '/(auth)/approval-queue'),
  business('Spending', 'Approval history', '/(auth)/approval-history'),
  business('Payroll', 'Payroll', '/(auth)/employee-roster'),
  business('Payroll', 'Run payroll', '/(auth)/payroll-review'),
  business('Payroll', 'Past payroll runs', '/(auth)/payroll-history'),
  business('Payroll', 'Payroll deductions', '/(auth)/payroll-deductions'),
  business('Payroll', 'Statutory settings', '/(auth)/statutory-settings'),
  business('Books & tax', 'Books', '/(auth)/books'),
  business('Books & tax', 'Review transactions', '/(auth)/categorisation-inbox'),
  business('Books & tax', 'Transactions', '/(auth)/transactions'),
  business('Books & tax', 'Full ledger', '/(auth)/full-ledger'),
  business('Books & tax', 'Reports', '/(auth)/reports-catalogue'),
  business('Books & tax', 'Tax hub', '/(auth)/tax-hub'),
  business('Books & tax', 'Tax & e-invoicing', '/(auth)/compliance-settings'),
  business('Books & tax', 'Compliance', '/(auth)/compliance-hub'),
  business('Books & tax', 'Statements', '/(auth)/statements-history'),
  business('Books & tax', 'Generate a statement', '/(auth)/statement-generation'),
  business('Books & tax', 'Receipts library', '/(auth)/receipts-library'),
  business('Books & tax', 'Unmatched receipts', '/(auth)/unmatched-receipts'),
  business('Team & account', 'Business profile', '/(auth)/business-profile'),
  business('Team & account', 'Account tiers & verification', '/(auth)/kyc-tiers'),
  business('Team & account', 'Team', '/(auth)/team-list'),
  business('Team & account', 'Roles', '/(auth)/role-list'),
  business('Team & account', 'Who can authorise payments', '/(auth)/signing-mandate'),
  business('Team & account', 'Accountant access', '/(auth)/accountant-access'),
  business('Team & account', 'Audit log', '/(auth)/audit-log'),
  business('Team & account', 'Plan & billing', '/(auth)/plan-selection'),
  business('Team & account', 'Your profile', '/(auth)/personal-profile'),
  business('Team & account', 'Settings', '/(auth)/settings'),
  business('Team & account', 'Security', '/(auth)/security-settings'),
  business('Team & account', 'Reset transaction PIN', '/(auth)/reset-transaction-pin'),
  business('Team & account', 'Notification settings', '/(auth)/notification-settings'),
  business('Team & account', 'Notifications', '/(auth)/notifications'),
  business('Help', 'Ibra insights', '/(auth)/ibra-insights'),
  business('Help', 'Chat with support', '/(auth)/support-conversation'),
  business('Help', 'Help centre', '/(auth)/support-home'),
  business('Help', 'Terms of service', '/(auth)/terms-of-service'),
  business('Help', 'Privacy policy', '/(auth)/privacy-policy'),
];

/** The apps a collection's articles are shown in. */
export function appsFor(audience: HelpAudience | undefined): AppName[] {
  if (audience === 'consumer') return ['consumer'];
  if (audience === 'business') return ['business'];
  return ['consumer', 'business'];
}

export function screensFor(audience: HelpAudience | undefined): AppScreen[] {
  const apps = appsFor(audience);
  return APP_SCREENS.filter((s) => apps.includes(s.app));
}

export function screenAt(route: string): AppScreen | undefined {
  return APP_SCREENS.find((s) => s.route === route);
}

export const APP_LABEL: Record<AppName, string> = {
  consumer: 'PayIQ app',
  business: 'PayIQ Business app',
};
